import { readOnlyAnnouncementID } from "#skyward_global";
import { ComponentInstance, computed, defineCustomElement, nextTick, onBeforeMount, onMounted, onUnmounted, ref, useHost, watch } from "vue";
import { nextAutoId } from "../../skyward.base.extensions";
import { DAYS_OF_WEEK, formatServerDate, now, parseDate } from "../../skyward.base.time";
import VueModalLayer from "../core/modalLayer";
import Svg from "../core/svg";
import { OptionalVueProp } from "../core/vueHelpers";
import { CalendarExposed } from "./components/Calendar";
import DatePickerModal from "./DatePickerModal";

const DatePickerClasses =
{
    input: "datePicker",
    svg: "dateTimePicker__svg",
    helpText: "datePicker__helpText"
};

enum DateParts
{
    Month = "M",
    Day = "D",
    Year = "Y"
}

const DatePicker = /*#__PURE__*/ defineCustomElement({
    name: "sky-datepicker",
    shadowRoot: false,
    inheritAttrs: false,
    props: {
        name: OptionalVueProp(String),
        value: {
            type: String,
            required: false,
            default: null,
            validator(value: any)
            {
                return !value || typeof value === "string" && parseDate(value).isValidDate();
            }
        },
        autocomplete: OptionalVueProp(String),
        dependents: OptionalVueProp(Object),
        ariaLabelledby: OptionalVueProp(String),
        tabindex: OptionalVueProp(Number),
        contained: OptionalVueProp(Boolean),
        hideDayOfWeek: {
            type: Boolean,
            default: false
        }
    },
    setup(props, context)
    {
        const inputRef = ref<HTMLInputElement>();
        const suppressNextFocus = ref<Boolean>(false);
        let rootElem = useHost() as any as (HTMLElement & { value: string | undefined }) | null;
        const resolveRootElem = () =>
        {
            if (!rootElem)
            {
                rootElem = inputRef.value?.closest("sky-datepicker") as (HTMLElement & { value: string | undefined }) | null;
            }
            return rootElem;
        };
        const getAriaReadonly = () => resolveRootElem()?.getAttribute("aria-readonly")?.toLowerCase() === "true";
        const readonly = ref(getAriaReadonly());
        // Read from the host element rather than declaring an "id" prop, which would shadow the native HTMLElement.id property
        const getHostId = () => resolveRootElem()?.id || undefined;
        const hostId = ref(getHostId());

        const value = computed<string | undefined>({
            get: () => props.value,
            set: (newValue) =>
            {
                const currentRoot = resolveRootElem();
                if (currentRoot)
                {
                    currentRoot.value = newValue;
                }
            }
        });

        watch(() => props.value, (newVal) =>
        {
            if (!(!newVal || typeof newVal === "string" && parseDate(newVal).isValidDate()))
            {
                value.value = "";
            }
        }, {
            flush: "sync",
            immediate: true
        });

        const valueAsDate = computed<Date | undefined>(
        {
            get: () =>
            {
                if (props.value === "" || props.value === null || props.value === undefined)
                {
                    return undefined;
                }
                const parsedDate = parseDate(props.value);
                return parsedDate.isValidDate() ? parsedDate : undefined;
            },
            set: (newValue) =>
            {
                let valueToSet = "";
                if (newValue?.isValidDate())
                {
                    newValue.setHours(0);
                    newValue.setMinutes(0);
                    newValue.setSeconds(0);
                    newValue.setMilliseconds(0);
                    valueToSet = formatServerDate(newValue, false);
                }
                value.value = valueToSet;
            }
            });

        // needs to be exposed as a function, otherwise Vue will deref it
        context.expose(
            {
                _valueAsDateRef: () => valueAsDate,
                _closeModal: closeModal
            });

        const modalOpen = ref<true | false | "closing">(false);

        onBeforeMount(() =>
        {
            if (hostId.value)
            {
                $.findLabel(hostId.value).attr("for", `${hostId.value}_input`);
            }
        });

        const observer = new MutationObserver(() =>
        {
            readonly.value = getAriaReadonly();
            hostId.value = getHostId();
        });

        onMounted(() =>
        {
            if (props.ariaLabelledby)
            {
                inputRef.value?.setAttribute("aria-labelledby", props.ariaLabelledby);
                resolveRootElem()?.removeAttribute("aria-labelledby");
            }

            const currentRoot = resolveRootElem();
            if (currentRoot)
            {
                observer.observe(currentRoot, { attributes: true, attributeFilter: ["aria-readonly", "id"] });
            }
        });

        onUnmounted(() =>
        {
            observer.disconnect();
        });

        const containerId = nextAutoId();
        const describedById = nextAutoId();
        const datepickerRef = ref<ComponentInstance<typeof DatePickerModal> & CalendarExposed>();

        return () =>
        (
            <>
                <input
                    type="text"
                    class={DatePickerClasses.input}
                    placeholder="MM/DD/YYYY"
                    readonly={readonly.value}
                    autocomplete={props.autocomplete ?? "off"}
                    ref={inputRef}
                    onClick={handleClick}
                    onFocusin={handleFocusIn}
                    onInput={handleInput}
                    onChange={handleChange}
                    onKeyup={handleKeyup}
                    onKeydown={handleKeydown}
                    onPaste={handlePaste}
                    value={formatDate(valueAsDate.value, props.hideDayOfWeek)}
                    id={hostId.value ? `${hostId.value}_input` : undefined}
                    data-dependents={JSON.stringify(props.dependents)}
                    role="combobox"
                    aria-expanded={modalOpen.value !== "closing" ? modalOpen.value : true}
                    aria-haspopup="dialog"
                    aria-controls={containerId}
                    aria-autocomplete="none"
                    aria-describedby={readonly.value ? readOnlyAnnouncementID : describedById}
                    tabindex={props.tabindex}
                />
                {
                    (modalOpen.value === true) &&
                    <VueModalLayer
                        containerId={containerId}
                        expose={[getModalAnchorElement()]}
                        anchorTo={getModalAnchorElement()}
                        preserveFocus={(reason) => reason === "escape"}
                        ariaLabel="Choose date"
                        onClose={closeModal}
                    >
                        <DatePickerModal
                            ref={datepickerRef}
                            inputRef={inputRef}
                            close={closeModal}
                            value={valueAsDate.value}
                            onUpdate:value={(newValue) =>
                            {
                                setValueFromUserInput(newValue);
                                closeModal();
                            }}
                        />
                    </VueModalLayer>
                }
                <Svg
                    name="Date"
                    aria-haspopup="menu"
                    role="button"
                    showHoverEffect={false}
                    class={DatePickerClasses.svg}
                    onClick={() => toggleModal("svg click")}
                    isButton={!readonly.value}
                    aria-label="Date Picker"
                />
                <div id={describedById} class={DatePickerClasses.helpText}>Type a date or use the down arrow key to navigate to the date picker</div>
            </>
        );

        function getModalAnchorElement()
        {
            const container = inputRef.value!.parentElement!;
            return props.contained
                ? container.parentElement!
                : container;
        }

        async function closeModal()
        {
            modalOpen.value = "closing";
            await nextTick();
            modalOpen.value = false;

            if (document.activeElement !== inputRef.value)
            {
                suppressNextFocus.value = true;
                inputRef.value?.focus();
            }

            if (value.value)
            {
                selectPart(DateParts.Month);
            }
        }

        function handlePaste(event: ClipboardEvent)
        {
            event.preventDefault();

            const pastedText = event.clipboardData?.getData('text/plain')
                .replace(new RegExp(`\\b(?:${DAYS_OF_WEEK.join("|")})\\b`, "g"), "");
            const date = new Date(pastedText ?? "");
            if (!pastedText || !date.isValidDate())
            {
                return false;
            }

            setValueFromUserInput(date);
            return false;
        }

        function handleFocusIn(event: FocusEvent)
        {
            const element = event.target as HTMLElement;
            const suppressFocus = element.getAttribute("data-suppress-focus") === "true" || resolveRootElem()?.getAttribute("data-suppress-focus") === "true";
            if (suppressFocus)
            {
                element.removeAttribute("data-suppress-focus");
                resolveRootElem()?.removeAttribute("data-suppress-focus");
                return;
            }
            if (suppressNextFocus.value)
            {
                suppressNextFocus.value = false;
                return;
            }
            handleOpenModal("focusin");
        }

        function selectPart(part: DateParts)
        {
            const input = inputRef.value!;
            const currentValue = input.value.replace(/[^\d/]/g, '');

            const firstSlash = currentValue.indexOf("/");
            const secondSlash = currentValue.indexOf("/", firstSlash + 1);
            const valueEnd = currentValue.length;

            let startSelection = null;
            let endSelection = null;

            switch (part)
            {
                case DateParts.Month:
                    startSelection = 0;
                    endSelection = firstSlash !== -1 ? firstSlash : valueEnd;
                    input.setSelectionRange(startSelection, endSelection); // Highlight MM
                    break;
                case DateParts.Day:
                    if (firstSlash !== -1)
                    {
                        startSelection = firstSlash + 1;

                        if (secondSlash !== -1)
                        {
                            endSelection = secondSlash;
                        }
                        else
                        {
                            endSelection = startSelection;
                        }

                        input.setSelectionRange(startSelection, endSelection); // Highlight DD
                    }
                    break;
                case DateParts.Year:
                    if (secondSlash !== -1)
                    {
                        startSelection = secondSlash + 1;
                        endSelection = valueEnd;

                        input.setSelectionRange(startSelection, endSelection); // Highlight YYYY
                    }
                    break;
            }
        }

        async function handleKeydown(event: KeyboardEvent)
        {
            const eventKeys = window.skyward.keys.getKeyNamesFromEvent(event);

            if (eventKeys.ARROW_RIGHT || eventKeys.ARROW_LEFT)
            {
                event.preventDefault();
                event.stopPropagation();
                const start = (event.target! as HTMLInputElement).selectionStart!;
                const end = (event.target! as HTMLInputElement).selectionEnd!;

                handleSelection(eventKeys.ARROW_LEFT, eventKeys.ARROW_RIGHT, start, end);
                return;
            }

            if (!modalOpen.value && eventKeys.ALT && (eventKeys.ARROW_DOWN || eventKeys.ARROW_UP))
            {
                handleOpenModal("keydown");
            }

            if (eventKeys.ENTER)
            {
                event.preventDefault();
                event.stopPropagation();

                if (modalOpen.value)
                {
                    modalOpen.value = false;
                }

                const date = parseDateInput(inputRef.value!.value.slice(0, 10));
                setValueFromUserInput(date);
                return;
            }

            if (modalOpen.value && eventKeys.TAB)
            {
                // Closing the modal will cause a re-render and reset our changes. We have to update now instead of waiting for onBlur
                modalOpen.value = false;
                const date = parseDateInput(inputRef.value!.value.slice(0, 10));
                setValueFromUserInput(date);
                return;
            }

            if (eventKeys.ARROW_UP || eventKeys.ARROW_DOWN)
            {
                event.preventDefault();
                event.stopPropagation();

                if (!modalOpen.value)
                {
                    handleOpenModal("keydown");
                    await nextTick();
                }

                datepickerRef.value?.focusCurrentDay();
            }
        }

        /**
         * Handle selecting the input range.
         * (Optional) based on a direction from your current selection
         * @param left true if moving left
         * @param right true if moving right
         */
        function handleSelection(left: boolean, right: boolean, start: number, end: number)
        {
            const input = inputRef.value!;
            const currentValue = input.value.replace(/[^\d/]/g, '');
            const firstSlash = currentValue.indexOf("/");
            const secondSlash = currentValue.indexOf("/", firstSlash + 1);

            // There are no parts to focus;
            if (firstSlash === -1)
            {
                return;
            }

            if (right)
            {
                if (isInRange(input, start, end, 0, firstSlash))
                {
                    selectPart(DateParts.Day);
                }
                else if (secondSlash !== -1 && isInRange(input, start, end, firstSlash + 1, secondSlash))
                {
                    selectPart(DateParts.Year);
                }
                else if (secondSlash !== -1 && isInRange(input, start, end, secondSlash + 1, input.value.length))
                {
                    selectPart(DateParts.Month);
                }
                return;
            }
            else if (left)
            {
                if (isInRange(input, start, end, 0, firstSlash))
                {
                    selectPart(DateParts.Year);
                }
                else if (isInRange(input, start, end, firstSlash + 1, input.value.length))
                {
                    if (secondSlash !== -1 && isInRange(input, start, end, secondSlash + 1, input.value.length))
                    {
                        selectPart(DateParts.Day);
                        return;
                    }
                    selectPart(DateParts.Month);
                }
                return;
            }

            // If no direction was specified, select based on current selection
            if (isInRange(input, start, end, 0, firstSlash))
            {
                selectPart(DateParts.Month);
            }
            else if (isInRange(input, start, end, firstSlash + 1, input.value.length))
            {
                if (secondSlash !== -1 && isInRange(input, start, end, secondSlash + 1, input.value.length))
                {
                    selectPart(DateParts.Year);
                    return;
                }
                selectPart(DateParts.Day);
            }
        }

        function handleKeyup(event: KeyboardEvent)
        {
            if (modalOpen.value || readonly.value)
            {
                return;
            }

            const eventKeys = window.skyward.keys.getKeyNamesFromEvent(event);

            // Handle shortcut keys (T = Today, R = Tomorrow, Y = Yesterday)
            if (eventKeys.T || eventKeys.R || eventKeys.Y)
            {
                const shortcutDate = now();
                if (eventKeys.R)
                {
                    shortcutDate.setDate(shortcutDate.getDate() + 1);
                }
                else if (eventKeys.Y)
                {
                    shortcutDate.setDate(shortcutDate.getDate() - 1);
                }

                setValueFromUserInput(shortcutDate);
            }
        }

        function toggleModal(cause: string)
        {
            if (modalOpen.value === true)
            {
                closeModal();
                return;
            }

            handleOpenModal(cause);
        }

        function handleOpenModal(cause: string)
        {
            if (readonly.value || modalOpen.value !== false)
            {
                return;
            }
            modalOpen.value = true;
            rootElem?.scrollIntoView({ block: 'nearest' });

            if (cause === "svg click" && inputRef.value)
            {
                inputRef.value.focus();
            }
        }

        function handleClick(event: MouseEvent)
        {
            const start = (event.target! as HTMLInputElement).selectionStart!;
            const end = (event.target! as HTMLInputElement).selectionEnd!;

            handleSelection(false, false, start, end);
        }

        function isInRange(_input: HTMLInputElement, start: number, end: number, startRange: number, endRange: number)
        {
            return (start >= startRange && end <= endRange);
        }

        function handleChange(event: Event)
        {
            event.preventDefault();
            event.stopPropagation();
            const date = parseDateInput(inputRef.value!.value.slice(0, 10));
            setValueFromUserInput(date);
        }

        async function handleInput(event: Event)
        {
            const input = event.target as HTMLInputElement;
            let selectionEnd = input.selectionEnd!;
            const newValue = input.value.replace(/[^\d/]/g, '');

            const parts = newValue.split("/");

            // If the user is attempting to enter more than 2 slashes, remove their selection entirely.
            if (parts.length > 3)
            {
                event.preventDefault();
                const inputEvent = event as InputEvent;
                const newData = inputEvent.data!;
                let valueToSet = newValue.slice(0, selectionEnd);
                valueToSet = valueToSet.slice(0, valueToSet.length - newData.length) + newValue.slice(selectionEnd);
                input.value = valueToSet;
                selectionEnd -= newData.length;
                input.setSelectionRange(selectionEnd, selectionEnd);
                return;
            }

            let remainingMonth = "";
            let remainingDay = "";
            if (parts.length > 0)
            {
                remainingMonth = parts[0].slice(2);
                parts[0] = parts[0].slice(0, 2);

                // Don't allow an invalid month to be entered
                const monthValue = parseInt(parts[0]);
                if (monthValue > 12)
                {
                    remainingMonth = parts[0].slice(1);
                    parts[0] = "0" + parts[0].slice(0, 1);
                }

                if (remainingMonth.length > 0 && parts.length < 2)
                {
                    parts.push(remainingMonth);
                }
            }
            if (parts.length > 1)
            {
                if (remainingMonth.length > 0)
                {
                    parts[1] = remainingMonth + parts[1].slice(1, 2);
                    selectionEnd = 4;
                }
                else
                {
                    if (selectionEnd != 5)
                    {
                        remainingDay = parts[1].slice(2);
                    }

                    parts[1] = parts[1].slice(0, 2);

                    // Don't allow an invalid day be entered
                    const daysInMonth = new Date(2000, parseInt(parts[0]), 0).getDate(); // Leap Year to allow for 29 (will be cast after change event if not a leap year)
                    const dayValue = parseInt(parts[1]);
                    if (dayValue > daysInMonth)
                    {
                        remainingDay = parts[1].slice(1);
                        parts[1] = "0" + parts[1].slice(0, 1);
                    }

                    if (remainingDay.length > 0 && parts.length < 3)
                    {
                        parts.push(remainingDay);
                    }
                }
            }
            if (parts.length > 2)
            {
                if (remainingDay.length > 0)
                {
                    parts[2] = remainingDay + parts[2].slice(1, 4);
                    selectionEnd = 7;
                }
                else
                {
                    parts[2] = parts[2].slice(0, 4);
                }
            }

            input.value = parts.join("/");
            await nextTick();

            input.setSelectionRange(selectionEnd, selectionEnd);
        }

        function setValueFromUserInput (newDate: Date | undefined)
        {
            inputRef.value!.value = formatDate(newDate, props.hideDayOfWeek) ?? "";

            if (valueAsDate.value?.valueOf() !== newDate?.valueOf())
            {
                valueAsDate.value = newDate;
                const event = new CustomEvent('change', { bubbles: true, detail: { internal: true } });
                resolveRootElem()?.dispatchEvent(event);
            }
        }
    }
});

class SkyDatePicker extends DatePicker
{
    connectedCallback()
    {
        // Clear any pre-existing inner HTML (e.g., serialized by CKEditor) before Vue mounts,
        // so that Vue's own render does not produce duplicate elements.
        if (!(this as any)._instance)
        {
            this.innerHTML = '';
        }

        super.connectedCallback();
    }

    get valueAsDate() { return (this as any)._valueAsDateRef().value; }
    set valueAsDate(newValue) { (this as any)._valueAsDateRef().value = newValue; }
    /**
     * Close the datepicker modal programatically.
     * This calls the function exposed from the component setup via `context.expose`.
     */
    closeModal(): Promise<void> | void
    {
        const fn = (this as any)._closeModal;
        if (typeof fn === "function")
        {
            return fn();
        }
    }
}
export default SkyDatePicker;

export function parseDateInput(value: string): Date | undefined
{
    const parts = value.split('/');
    const currentDate = now();
    let month: number;
    let day: number;
    let year: number = currentDate.getFullYear();

    if (parts.length > 0)
    {
        month = parseInt(parts[0]);
        if (isNaN(month))
        {
            return undefined;
        }
        else if (month < 1)
        {
            month = 1;
        }
        else if (month > 12)
        {
            month = 12;
        }
    }
    else
    {
        return undefined;
    }

    if (parts.length > 1)
    {
        day = parseInt(parts[1]);
        if (isNaN(day) || day < 1)
        {
            day = 1; // Default to the first day of the month
        }
    }
    else
    {
        day = 1; // Default to the first day of the month if day is not provided
    }

    if (parts.length > 2)
    {
        // Do some year inference
        const setYear = parts[2];
        if (setYear.length < 3 && setYear.length > 0)
        {
            const yearValue = parseInt(setYear);
            if (yearValue > 55)
            {
                parts[2] = `19${setYear}`;
            }
            else
            {
                const partToPrepend = setYear.length === 2 ? "20" : "200"
                parts[2] = partToPrepend + setYear;
            }
        }

        year = parseInt(parts[2]);

        // Fallback
        if (isNaN(year))
        {
            year = currentDate.getFullYear();
        }
    }

    // Clamp to the last valid day of the entered month/year, after the year is parsed.
    // Validates leap year dates
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day > daysInMonth)
    {
        day = daysInMonth; // Cast to the last day of the month
    }

    const date = new Date(year, month - 1, day); // month is zero-based in Date constructor
    date.setUTCFullYear(year); // The JS Date constructor doesn't always honor your year value and may cast it.
    return date;
}

export function formatDate(date?: Date, hideDayOfWeek = false)
{
    if (!date || !date.isValidDate())
    {
        return undefined;
    }

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = String(date.getFullYear()).padStart(4, "0");
    const formattedDate = `${month}/${day}/${year}`;

    return hideDayOfWeek
        ? formattedDate
        : `${formattedDate} ${DAYS_OF_WEEK[date.getDay()]}`;
}
